import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FaTimes, FaWallet, FaEdit, FaCheckCircle, FaUniversity, FaShieldAlt } from "react-icons/fa";
import {
  requestUserWithdrawal,
  getBankDetails,
  getUserWallet,
  getUserTransactions,
} from "../../features/wallet/walletSlice";
import UpdateBankDetailsModal from "./UpdateBankDetailsModal";

const WithdrawMoneyModal = ({ isOpen, onClose, maxAmount }) => {
  const dispatch = useDispatch();
  const { isLoading, bankDetails } = useSelector((state) => state.wallet);

  const [amount, setAmount] = useState("");
  const [accountName, setAccountName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [ifscCode, setIfscCode] = useState("");
  const [bankName, setBankName] = useState("");
  const [errors, setErrors] = useState({});
  const [showBankDetailsModal, setShowBankDetailsModal] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(true);

  // Fetch saved bank details when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsLoadingDetails(true);
      dispatch(getBankDetails()).finally(() => {
        setIsLoadingDetails(false);
      });
    }
  }, [isOpen, dispatch]);

  // Populate form with saved bank details if they exist
  useEffect(() => {
    if (bankDetails) {
      setAccountName(bankDetails.accountName || "");
      setAccountNumber(bankDetails.accountNumber || "");
      setIfscCode(bankDetails.ifscCode || "");
      setBankName(bankDetails.bankName || "");
    }
  }, [bankDetails]);

  const validateForm = () => {
    const newErrors = {};

    if (!amount || isNaN(amount) || amount <= 0) {
      newErrors.amount = "Please enter a valid amount";
    } else if (amount < 100) {
      newErrors.amount = "Minimum withdrawal is ₹100";
    } else if (amount > maxAmount) {
      newErrors.amount = `Amount cannot exceed ₹${maxAmount.toFixed(2)}`;
    }

    if (!bankDetails) {
      if (!accountName.trim()) {
        newErrors.accountName = "Account name is required";
      }
      if (!accountNumber.trim() || accountNumber.length < 9) {
        newErrors.accountNumber = "Valid account number required";
      }
      if (!ifscCode.trim() || ifscCode.length < 4) {
        newErrors.ifscCode = "Valid IFSC required";
      }
      if (!bankName.trim()) {
        newErrors.bankName = "Bank name required";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleWithdraw = async () => {
    if (!validateForm()) return;

    const withdrawalPayload = {
      amount: parseInt(amount),
    };

    if (!bankDetails) {
      withdrawalPayload.bankDetails = {
        accountName,
        accountNumber,
        ifscCode: ifscCode.toUpperCase(),
        bankName,
      };
    }

    const resultAction = await dispatch(requestUserWithdrawal(withdrawalPayload));

    if (requestUserWithdrawal.fulfilled.match(resultAction)) {
      setAmount("");
      setErrors({});
      setTimeout(() => {
        dispatch(getUserWallet());
        dispatch(getUserTransactions({ page: 1 }));
      }, 500);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-start justify-center p-3 sm:p-4 pt-20 sm:pt-24 overflow-y-auto">
        <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full p-6 sm:p-7 relative border border-slate-100">
          {/* Header - Compact */}
          <div className="flex items-center justify-between pb-2.5 border-b border-slate-100 mb-3.5">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center">
                <FaWallet size={15} />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-900 leading-tight">Withdraw Money</h2>
                <p className="text-[11px] text-slate-500">Transfer withdrawable balance to your bank account</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
              aria-label="Close"
            >
              <FaTimes size={15} />
            </button>
          </div>

          {/* Horizontal 2-Column Content Grid - Compact */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
            {/* LEFT COLUMN: Balance + Amount + Info */}
            <div className="flex flex-col gap-3">
              {/* Available Balance Box */}
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200/80 rounded-xl p-3">
                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide block">
                  Available to Withdraw
                </span>
                <span className="text-xl sm:text-2xl font-black text-orange-600 tracking-tight block">
                  ₹{maxAmount.toFixed(2)}
                </span>
                <span className="text-[10px] text-slate-500 block">
                  Earnings & unencumbered funds
                </span>
              </div>

              {/* Amount Input Field */}
              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wide mb-1">
                  Withdrawal Amount (₹)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs">
                    ₹
                  </span>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="Enter amount (min ₹100)"
                    className={`w-full pl-7 pr-3 py-2 rounded-xl border text-sm font-semibold transition-all ${
                      errors.amount
                        ? "border-red-500 focus:ring-2 focus:ring-red-100"
                        : "border-slate-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/10"
                    }`}
                    min="100"
                    max={maxAmount}
                  />
                </div>
                {errors.amount && (
                  <p className="text-red-500 text-[11px] mt-0.5 font-medium">{errors.amount}</p>
                )}
                <div className="flex items-center justify-between text-[10px] text-slate-400 mt-1">
                  <span>Min: ₹100</span>
                  {maxAmount > 0 && (
                    <button
                      type="button"
                      onClick={() => setAmount(Math.floor(maxAmount).toString())}
                      className="text-orange-600 hover:text-orange-700 font-bold"
                    >
                      Max (₹{Math.floor(maxAmount)})
                    </button>
                  )}
                </div>
              </div>

              {/* Transfer Note */}
              <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-2.5 text-[11px] text-slate-600 space-y-0.5">
                <div className="flex items-center gap-1 font-bold text-slate-800 text-[10px] uppercase tracking-wide">
                  <FaShieldAlt className="text-emerald-500" size={10} />
                  <span>Transfer Info</span>
                </div>
                <p className="text-[10px] leading-snug text-slate-500">
                  Transferred via IMPS/NEFT within 2-3 business days to registered bank account.
                </p>
              </div>
            </div>

            {/* RIGHT COLUMN: Bank Details + Action Buttons */}
            <div className="flex flex-col gap-3">
              {isLoadingDetails ? (
                <div className="animate-pulse bg-slate-100 rounded-xl h-36"></div>
              ) : bankDetails ? (
                /* Saved Bank Details Card */
                <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
                  <div className="flex items-center justify-between pb-2 border-b border-slate-200 mb-2">
                    <div className="flex items-center gap-1.5">
                      <FaCheckCircle className="text-emerald-500 text-sm" />
                      <span className="font-bold text-[11px] text-slate-900 uppercase tracking-wide">Bank Account Saved</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowBankDetailsModal(true)}
                      className="text-orange-600 hover:text-orange-700 inline-flex items-center gap-1 text-[11px] font-bold transition"
                    >
                      <FaEdit size={10} />
                      <span>Edit</span>
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-slate-400 block text-[9px] uppercase font-bold">Holder</span>
                      <span className="font-bold text-slate-800 block truncate text-xs">{bankDetails.accountName}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block text-[9px] uppercase font-bold">Bank</span>
                      <span className="font-bold text-slate-800 block truncate text-xs">{bankDetails.bankName}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block text-[9px] uppercase font-bold">Account</span>
                      <span className="font-mono font-bold text-slate-800 block text-xs">
                        {bankDetails.accountNumber.slice(-4).padStart(bankDetails.accountNumber.length, "•")}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-400 block text-[9px] uppercase font-bold">IFSC</span>
                      <span className="font-mono font-bold text-slate-800 block text-xs">{bankDetails.ifscCode}</span>
                    </div>
                  </div>
                </div>
              ) : (
                /* First-Time Bank Input Form (2x2 Grid) */
                <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
                  <div className="flex items-center gap-1.5 mb-2">
                    <FaUniversity className="text-slate-500" size={11} />
                    <span className="font-bold text-[11px] text-slate-900 uppercase tracking-wide">Enter Bank Details</span>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] font-semibold text-slate-600 mb-0.5">Account Holder</label>
                      <input
                        type="text"
                        value={accountName}
                        onChange={(e) => setAccountName(e.target.value)}
                        placeholder="Name as in passbook"
                        className={`w-full px-2.5 py-1.5 rounded-lg border text-xs ${
                          errors.accountName ? "border-red-500" : "border-slate-300"
                        }`}
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] font-semibold text-slate-600 mb-0.5">Bank Name</label>
                      <input
                        type="text"
                        value={bankName}
                        onChange={(e) => setBankName(e.target.value)}
                        placeholder="e.g. HDFC / SBI"
                        className={`w-full px-2.5 py-1.5 rounded-lg border text-xs ${
                          errors.bankName ? "border-red-500" : "border-slate-300"
                        }`}
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] font-semibold text-slate-600 mb-0.5">Account Number</label>
                      <input
                        type="text"
                        value={accountNumber}
                        onChange={(e) => setAccountNumber(e.target.value)}
                        placeholder="Account number"
                        className={`w-full px-2.5 py-1.5 rounded-lg border text-xs font-mono ${
                          errors.accountNumber ? "border-red-500" : "border-slate-300"
                        }`}
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] font-semibold text-slate-600 mb-0.5">IFSC Code</label>
                      <input
                        type="text"
                        value={ifscCode}
                        onChange={(e) => setIfscCode(e.target.value.toUpperCase())}
                        placeholder="e.g. HDFC0000001"
                        className={`w-full px-2.5 py-1.5 rounded-lg border text-xs font-mono uppercase ${
                          errors.ifscCode ? "border-red-500" : "border-slate-300"
                        }`}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center gap-2.5 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-2 px-3 rounded-xl transition text-xs"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleWithdraw}
                  disabled={isLoading || !amount}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-bold py-2 px-3 rounded-xl transition shadow-md shadow-orange-500/20 text-xs disabled:bg-slate-300 disabled:cursor-not-allowed disabled:shadow-none"
                >
                  {isLoading ? "Processing..." : "Request Withdrawal"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Update Bank Details Modal */}
      <UpdateBankDetailsModal
        isOpen={showBankDetailsModal}
        onClose={() => setShowBankDetailsModal(false)}
        existingDetails={bankDetails}
      />
    </>
  );
};

export default WithdrawMoneyModal;
